import { queryOptions } from '@tanstack/react-query';
import type { drivelabels_v2 } from '@googleapis/drivelabels';
import instance from './axios-instance';
import { doApiCheck, UserKeys } from './users-api';

export type DriveLabel = drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label;

export type DriveLabelPermission =
  drivelabels_v2.Schema$GoogleAppsDriveLabelsV2LabelPermission;

/** Google label roles that the Label permissions dialog can grant. */
/**
 * Google's label roles. The dialog offers READER and APPLIER, like the Admin console; ORGANIZER and
 * EDITOR can only be set through the API, so they're kept and shown but never offered for new people.
 */
export type LabelPermissionRole = 'READER' | 'APPLIER' | 'ORGANIZER' | 'EDITOR';

/** Mirrors com.promevo.gpanel.shared.administration.labels.LabelPermissionModel. */
export interface LabelPermissionModel {
  principalType: 'AUDIENCE' | 'USER' | 'GROUP';
  email: string;
  role: LabelPermissionRole;
}

export type LabelFieldType =
  | 'BADGE_LIST'
  | 'SINGLE_SELECT'
  | 'MULTI_SELECT'
  | 'NUMBER'
  | 'DATE'
  | 'TEXT'
  | 'PERSON';

export type LabelCopyMode = 'DO_NOT_COPY' | 'ALWAYS_COPY' | 'COPY_APPLIABLE';

/** Mirrors com.promevo.gpanel.shared.administration.labels.LabelOptionModel. */
export interface LabelOptionModel {
  id: string;
  name: string;
  description: string;
  badgeColor: string;
  /** Edit Label: the option is disabled, so it can't be selected any more. */
  disabled?: boolean;
  /** Edit Label, disabled options: still shown (not selectable) when applying, or hidden. */
  showInApply?: boolean;
}

/** Mirrors com.promevo.gpanel.shared.administration.labels.LabelFieldModel. */
export type LabelDateFormat = 'LONG_DATE' | 'SHORT_DATE';

export interface LabelFieldModel {
  id: string;
  type: LabelFieldType;
  name: string;
  required: boolean;
  /** Date fields only. */
  dateFormat: LabelDateFormat;
  /** Person fields only: users can pick more than one person. */
  multipleSelections: boolean;
  options: LabelOptionModel[];
  /** Edit Label: the field is disabled, so it can't be applied any more. */
  disabled?: boolean;
}

/** Mirrors com.promevo.gpanel.shared.administration.labels.LabelDraftModel. */
export interface LabelDraftModel {
  title: string;
  description: string;
  learnMoreUri: string;
  copyMode: LabelCopyMode;
  driveEnabled: boolean;
  gmailEnabled: boolean;
  fields: LabelFieldModel[];
}

export const DriveLabelKeys = {
  WriteAccess: 'driveLabelsWrite',
  EditLabel: 'driveLabelEdit',
};

/**
 * Checks whether the domain has granted gPanel the Drive Labels write scope. Shares its cache
 * entry with the Google API Status page. No error meta, so a missing scope doesn't show an error
 * dialog; callers show ScopesAlert instead.
 */
export const driveLabelsWriteAccessQuery = () =>
  queryOptions({
    queryKey: [UserKeys.AdminApiSettings, DriveLabelKeys.WriteAccess],
    queryFn: () => doApiCheck(DriveLabelKeys.WriteAccess, null),
  });

/** The label a clone was made from, recorded in the create audit log. */
export interface ClonedFromLabel {
  id: string;
  title: string;
}

export async function createDriveLabelDraft(
  draft: LabelDraftModel,
  changeLogMessage = '',
  clonedFrom?: ClonedFromLabel
) {
  const res = await instance.post<DriveLabel>('/api/labels/create', draft, {
    params: {
      changeLogMessage,
      ...(clonedFrom && {
        clonedFromLabelId: clonedFrom.id,
        clonedFromTitle: clonedFrom.title,
      }),
    },
  });
  return res.data;
}

export async function saveDriveLabelDraft(
  labelId: string,
  draft: LabelDraftModel
) {
  const res = await instance.put<DriveLabel>(
    `/api/labels/${encodeURIComponent(labelId)}/draft`,
    draft
  );
  return res.data;
}

export async function publishDriveLabel(
  labelId: string,
  draft: LabelDraftModel | null,
  changeLogMessage = ''
) {
  const res = await instance.post<DriveLabel>(
    `/api/labels/${encodeURIComponent(labelId)}/publish`,
    draft,
    {
      params: { changeLogMessage },
      headers: { 'Content-Type': 'application/json' },
    }
  );
  return res.data;
}

/** Replaces the permissions of the label draft being created; applies without publishing. */
export async function setDriveLabelPermissions(
  labelId: string,
  permissions: LabelPermissionModel[],
  changeLogMessage = ''
) {
  const res = await instance.put<DriveLabelPermission[]>(
    `/api/labels/${encodeURIComponent(labelId)}/permissions`,
    permissions,
    { params: { changeLogMessage } }
  );
  return res.data;
}

export interface LabelActionResult {
  labelId: string;
  success: boolean;
  error: string;
}

export async function bulkDeleteDriveLabels(
  labelIds: string[],
  changeLogMessage = ''
) {
  const res = await instance.post<LabelActionResult[]>(
    '/api/labels/bulkDelete',
    { labelIds },
    { params: { changeLogMessage } }
  );
  return res.data;
}

export interface DisableLabelOptions {
  hideInSearch?: boolean;
  showInApply?: boolean;
}

export async function bulkDisableDriveLabels(
  labelIds: string[],
  options: DisableLabelOptions = {},
  changeLogMessage = ''
) {
  const res = await instance.post<LabelActionResult[]>(
    '/api/labels/bulkDisable',
    { labelIds, ...options },
    { params: { changeLogMessage } }
  );
  return res.data;
}

export async function bulkEnableDriveLabels(
  labelIds: string[],
  changeLogMessage = ''
) {
  const res = await instance.post<LabelActionResult[]>(
    '/api/labels/bulkEnable',
    { labelIds },
    { params: { changeLogMessage } }
  );
  return res.data;
}

/** Edit Label: the label's latest revision, including changes saved but not yet published. */
export async function getDriveLabelForEdit(labelId: string, published = false) {
  const res = await instance.get<DriveLabel>(
    `/api/labels/${encodeURIComponent(labelId)}/edit`,
    { params: { published } }
  );
  return res.data;
}

export const getDriveLabelForEditQuery = (labelId: string) =>
  queryOptions({
    queryKey: [UserKeys.DriveLabels, labelId, DriveLabelKeys.EditLabel],
    queryFn: () => getDriveLabelForEdit(labelId),
    enabled: !!labelId,
  });

/** Edit Label: the published revision, shown in the preview's Published tab. */
export const getPublishedDriveLabelQuery = (
  labelId: string,
  enabled: boolean
) =>
  queryOptions({
    queryKey: [
      UserKeys.DriveLabels,
      labelId,
      DriveLabelKeys.EditLabel,
      'published',
    ],
    queryFn: () => getDriveLabelForEdit(labelId, true),
    enabled: !!labelId && enabled,
  });

/** Saves Edit Label's changes as the label's draft; users don't see them until published. */
export async function saveDriveLabelEdit(
  labelId: string,
  draft: LabelDraftModel
) {
  const res = await instance.put<DriveLabel>(
    `/api/labels/${encodeURIComponent(labelId)}/edit`,
    draft
  );
  return res.data;
}

/** Saves Edit Label's changes and publishes them. */
export async function publishDriveLabelEdit(
  labelId: string,
  draft: LabelDraftModel,
  changeLogMessage = ''
) {
  const res = await instance.post<DriveLabel>(
    `/api/labels/${encodeURIComponent(labelId)}/edit/publish`,
    draft,
    { params: { changeLogMessage } }
  );
  return res.data;
}

/** Replaces the permissions of a label being edited; applies without publishing. */
export async function setDriveLabelPermissionsForEdit(
  labelId: string,
  permissions: LabelPermissionModel[],
  changeLogMessage = ''
) {
  const res = await instance.put<DriveLabelPermission[]>(
    `/api/labels/${encodeURIComponent(labelId)}/edit/permissions`,
    permissions,
    { params: { changeLogMessage } }
  );
  return res.data;
}
